<!--
  OrderDetailView -- presentational, props-driven order-detail renderer.

  DESIGN INTENT (Jake, 2026-06-30): this component renders ONLY the order
  data it is GIVEN via props. It has NO fields, slots, or branches for
  distributor-internal data (margin, supplier/blank cost, app_fee, TF cut).
  Because it renders only passed-in data and has no internal-field surface,
  it is safe-by-construction for the Client Portal AND reusable later by the
  distributor ResponsiveOrderView (which will pass a superset of fields and wrap this
  in its own SEPARATE internal panel -- a FOLLOW-UP PR, not this one).

  This component is intentionally free of any data fetching, routing, or
  tenant context. The caller (OrderDetailDialog for the portal) resolves and
  passes client-safe fields. Money is passed in cents and formatted here.

  INTERNAL SLOT (added for the distributor live-order surface): this component
  exposes a named ``#internal`` slot rendered at the very bottom. The slot is
  the distributor ResponsiveOrderView's hook for its SEPARATE internal panel
  (OrderProfitBreakdown). OrderDetailView NEVER populates this slot itself and
  declares NO internal-field props -- it stays client-safe by construction. The
  portal caller leaves the slot empty, so no internal content can appear there.

  Reference layout: the v2 client-portal design
  (planning/design-handoffs/handoff_2026-06-24/.../design/sections-core.jsx
  Orders/Expanded).
-->
<template>
  <div class="order-detail-view" data-testid="order-detail-view">
    <!-- Header: order # + status + date -->
    <div class="odv-header">
      <div>
        <div class="odv-order-number" data-testid="odv-order-number">
          {{ order.order_number || "Order" }}
        </div>
        <div
          v-if="placedAtText"
          class="odv-date text-medium-emphasis"
          data-testid="odv-date"
        >
          Placed {{ placedAtText }}
        </div>
      </div>
      <span
        class="odv-status-chip"
        :class="statusChipClass(order.status)"
        data-testid="odv-status"
      >
        {{ statusLabel(order.status) }}
      </span>
    </div>

    <!-- Meta grid: shopper / department / store -->
    <div class="odv-meta">
      <div>
        <div class="odv-sublabel">Shopper</div>
        <div class="odv-value" data-testid="odv-shopper">
          {{ order.shopper_name || order.placed_by_email || "Unknown shopper" }}
        </div>
        <div
          v-if="order.shopper_email"
          class="odv-value odv-shopper-email"
          data-testid="odv-shopper-email"
        >{{ order.shopper_email }}</div>
      </div>
      <div>
        <div class="odv-sublabel">Department</div>
        <div class="odv-value" data-testid="odv-department">
          {{ order.department || "--" }}
        </div>
      </div>
      <div>
        <div class="odv-sublabel">Store</div>
        <div class="odv-value" data-testid="odv-store">
          {{ order.store_name || "--" }}
        </div>
      </div>
    </div>

    <!-- Optional client-safe checkout fields (rendered only when the caller
         passes them). These are the campaign-configured shopper-facing fields
         the buyer filled in at checkout (e.g. Company Email, Location) -- all
         client-safe, none distributor-internal. The distributor live view uses
         this to preserve the dynamic "Shopper Details" it historically showed;
         the portal caller does not pass it, so the section stays hidden there. -->
    <div
      v-if="checkoutFields && checkoutFields.length"
      class="odv-section"
      data-testid="odv-checkout-fields"
    >
      <div class="odv-sublabel">Shopper details</div>
      <div class="odv-meta">
        <div
          v-for="(field, i) in checkoutFields"
          :key="i"
          :data-testid="`odv-checkout-field-${i}`"
        >
          <div class="odv-sublabel">{{ field.label }}</div>
          <div class="odv-value">{{ field.value || "--" }}</div>
        </div>
      </div>
    </div>

    <!-- Line items -->
    <div class="odv-section" data-testid="odv-items">
      <div class="odv-sublabel">Items</div>
      <div
        v-if="!(order.items && order.items.length)"
        class="text-body-2 text-medium-emphasis"
        data-testid="odv-items-empty"
      >
        No line items recorded.
      </div>
      <div
        v-for="(item, i) in order.items"
        :key="i"
        class="odv-item"
        :data-testid="`odv-item-${i}`"
      >
        <span class="odv-item-desc">
          {{ item.quantity }} &times; {{ item.product_name || "Item" }}
          <span v-if="item.variant" class="text-medium-emphasis"
            >({{ item.variant }})</span
          >
        </span>
        <span
          v-if="item.unit_price_cents != null"
          class="odv-item-price"
          :data-testid="`odv-item-price-${i}`"
        >
          {{ formatDollars(item.unit_price_cents) }}
        </span>
      </div>
    </div>

    <!-- Shipping -->
    <div class="odv-section" data-testid="odv-shipping">
      <div class="odv-sublabel">Shipping</div>
      <div v-if="order.shipping" class="text-body-2">
        <div>{{ order.shipping.name || order.shopper_name }}</div>
        <div class="text-medium-emphasis">
          {{ shippingAddress(order.shipping) }}
        </div>
        <div
          v-if="order.shipping.status"
          class="text-medium-emphasis"
          data-testid="odv-ship-status"
        >
          Ship status: {{ statusLabel(order.shipping.status) }}
        </div>

        <!--
          Tracking: the shared renderer, so the storefront order page shows
          an order's boxes under exactly the same rules. `shipments` is
          normalized to [] below, so an order with a shipping block always
          gets the "no tracking yet" line rather than nothing.
        -->
        <OrderShipments :shipments="shipments" class="mt-2">
          <template #label>
            <div class="odv-sublabel">Tracking</div>
          </template>
        </OrderShipments>
      </div>
      <div
        v-else-if="shippingUnavailable"
        class="text-body-2 text-medium-emphasis"
        data-testid="odv-shipping-unavailable"
      >
        Shipping details could not be loaded.
      </div>
      <div v-else class="text-body-2 text-medium-emphasis">
        No shipping details on file.
      </div>
    </div>

    <!-- Totals -->
    <div class="odv-totals" data-testid="odv-totals">
      <div
        v-if="paymentLabel"
        class="odv-total-row"
        data-testid="odv-payment"
      >
        <span class="text-medium-emphasis">Payment: {{ paymentLabel }}</span>
      </div>
      <div v-if="order.shipping_cents != null" class="odv-total-row">
        <span class="text-medium-emphasis">Shipping</span>
        <span class="odv-cell--mono">{{
          formatDollars(order.shipping_cents)
        }}</span>
      </div>
      <div class="odv-total-row odv-total-row--grand">
        <span>Total</span>
        <span class="odv-cell--mono" data-testid="odv-total">{{
          formatDollars(order.total_cents)
        }}</span>
      </div>
    </div>

    <!-- Funding breakdown (rendered ONLY when the caller passes it) -->
    <div
      v-if="funding && funding.length"
      class="odv-section"
      data-testid="odv-funding"
    >
      <div class="odv-sublabel">{{ fundingLabel }}</div>
      <div
        v-for="(band, i) in funding"
        :key="i"
        class="odv-total-row"
        :data-testid="`odv-funding-${i}`"
      >
        <span class="text-medium-emphasis">{{ band.label }}</span>
        <span class="odv-cell--mono">{{
          formatDollars(band.amount_cents)
        }}</span>
      </div>
    </div>

    <!-- Distributor-internal slot. Rendered at the bottom, wrapped so the
         host element is queryable. OrderDetailView contributes NO content of
         its own here; only a caller (the distributor ResponsiveOrderView) fills it. The
         portal leaves it empty, keeping this component client-safe. -->
    <div class="odv-internal-slot" data-testid="odv-internal-slot">
      <slot name="internal" />
    </div>
  </div>
</template>

<script>
import OrderShipments from "./OrderShipments.vue";

// Portal status vocabulary -> human label. Kept in sync with the backend
// portal serializer (_map_order_status) and the Orders list page.
const STATUS_LABELS = {
  pending: "Placed",
  in_production: "In production",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  // Distributor detail page only: the raw status reaches it unmasked. The
  // portal never sends this value (its serializer maps it to "pending").
  decorator_cancelled: "Decorator cancelled",
};

// Status -> chip style class. Maps to the design's StatusChip fg/bg pairs
// (info / warning / success / neutral), each using the AA-safe *-dark text
// tokens on the soft background so the label is full-contrast (not the muted
// tonal derivation Vuetify produced before this polish pass).
const STATUS_CHIP_CLASS = {
  pending: "odv-status-chip--info",
  in_production: "odv-status-chip--warning",
  shipped: "odv-status-chip--success",
  delivered: "odv-status-chip--success",
  cancelled: "odv-status-chip--neutral",
  decorator_cancelled: "odv-status-chip--neutral",
};

export default {
  name: "OrderDetailView",
  components: { OrderShipments },
  props: {
    // The order to render. Shape: the client-safe subset of the
    // /api/portal/orders per-order payload. This component NEVER reads any
    // distributor-internal key -- there is no code path that renders margin /
    // cost / app_fee / TF cut even if such a key were present on the object.
    order: {
      type: Object,
      required: true,
    },
    // Optional funding breakdown: [{ label, amount_cents }]. Rendered only
    // when passed; the portal list payload does not include it today, so the
    // section stays hidden there.
    funding: {
      type: Array,
      default: null,
    },
    // Optional client-safe checkout fields: [{ label, value }]. These are the
    // campaign-configured, shopper-facing fields captured at checkout. Rendered
    // only when passed. This is a CLIENT-SAFE field -- it never carries
    // distributor-internal cost/margin data. The portal caller omits it.
    checkoutFields: {
      type: Array,
      default: null,
    },
    // Optional: how the order was paid ("Net Checkout", "Card", ...). A
    // status of "processing" reads the same for a card-paid order and one
    // that never touched a card, so the distributor page passes the order's
    // frozen payment label (Bug Intake #518). Hidden when empty.
    paymentLabel: {
      type: String,
      default: "",
    },
    // Heading for the optional funding/breakdown section. Defaults to
    // "Funding" (portal usage). The distributor live view passes
    // "Price breakdown" to label its client-safe price bands. Purely
    // presentational -- no data-flow or client-safety implication.
    fundingLabel: {
      type: String,
      default: "Funding",
    },
    // Set by a caller that loads shipping in a SEPARATE read which failed
    // (the distributor detail page's fulfillment read). An absent
    // `order.shipping` then means "unknown", not "none on file", so the
    // section says it could not be loaded instead of making a false claim.
    // Only consulted when `order.shipping` is absent; callers whose order
    // payload carries shipping inline (portal, tokenized storefront view)
    // never pass it and keep "No shipping details on file."
    shippingUnavailable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    placedAtText() {
      if (!this.order.placed_at) return "";
      const d = new Date(this.order.placed_at);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    // Shipment tracking rows (card https://trello.com/c/AdJtbgH6), carried
    // on the shipping block by both the portal payload and the tokenized
    // guest-order payload. Normalized to an array here so the template
    // never has to guard on a missing key -- an order placed before this
    // feature, or one whose API response predates it, simply has none.
    shipments() {
      const shipping = this.order && this.order.shipping;
      if (!shipping || !Array.isArray(shipping.shipments)) return [];
      return shipping.shipments;
    },
  },
  methods: {
    statusLabel(status) {
      return STATUS_LABELS[status] || status || "Placed";
    },
    statusChipClass(status) {
      return STATUS_CHIP_CLASS[status] || "odv-status-chip--neutral";
    },
    shippingAddress(ship) {
      const parts = [
        ship.address1,
        ship.address2,
        [ship.city, ship.state].filter(Boolean).join(", "),
        ship.postal_code,
      ].filter(Boolean);
      return parts.join(" ");
    },
    formatDollars(cents) {
      return `$${((cents || 0) / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
};
</script>

<style scoped>
.order-detail-view {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.odv-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.odv-order-number {
  font-family: var(--tf-font-mono, monospace);
  font-size: 16px;
  font-weight: 600;
  color: var(--tf-fg-1, #212121);
}
.odv-date {
  font-size: 12px;
  margin-top: 2px;
}
.odv-meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.odv-sublabel {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--tf-fg-4, #757575);
  margin-bottom: 6px;
}
.odv-value {
  font-size: 14px;
  color: var(--tf-fg-1, #212121);
}
.odv-shopper-email {
  font-size: 13px;
  color: var(--tf-fg-3, #616161);
  overflow-wrap: anywhere;
}
.odv-section {
  border-top: 1px solid var(--tf-border, #e0e0e0);
  padding-top: 14px;
}
.odv-item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  line-height: 1.7;
}
.odv-item-price {
  font-family: var(--tf-font-mono, monospace);
  font-size: 13px;
  white-space: nowrap;
}
.odv-totals {
  border-top: 1px solid var(--tf-border, #e0e0e0);
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.odv-total-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
}
.odv-total-row--grand {
  font-size: 15px;
  font-weight: 700;
  color: var(--tf-fg-1, #212121);
}
.odv-cell--mono {
  font-family: var(--tf-font-mono, monospace);
}

/* Status chip: explicit AA-safe fg on soft bg, pill radius. Matches the v2
   design's StatusChip fg/bg pairs. Full-contrast text (the *-dark tokens),
   NOT the muted tonal derivation. */
.odv-status-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  line-height: 1.4;
  padding: 3px 10px;
  border-radius: var(--tf-radius-pill, 9999px);
  white-space: nowrap;
}
.odv-status-chip--info {
  color: var(--tf-chip-info-fg, #1565c0);
  background: var(--tf-chip-info-bg, #e3f2fd);
}
.odv-status-chip--warning {
  color: var(--tf-warning-dark, #8a6d3b);
  background: var(--tf-warning-soft, #fff8e1);
}
.odv-status-chip--success {
  color: var(--tf-success-dark, #2e7d32);
  background: var(--tf-success-soft, #e6f7ed);
}
.odv-status-chip--neutral {
  color: var(--tf-fg-3, #616161);
  background: var(--tf-chip-neutral-bg, #f1f3f5);
}
@media (max-width: 600px) {
  .odv-meta {
    grid-template-columns: 1fr;
  }
}
</style>
